import { DateTime } from 'luxon';

export interface SortFn {
  (a: any, b: any): number;
}

export enum SORT_DIRECTION {
  'asc' = 1,
  'desc' = -1,
}

export interface SortOptions {
  type?: 'string' | 'number' | 'boolean' | 'date';
}

export class Sort {
  /**
   * Sort an array of objects by a property or a function
   * @example
   *  const items = [{ name: 'in', lastname: 'wakwak' }, { name: 'sik', lastname: 'kuyamad }];
   *  items.sort(Sort.by('name'));
   *
   *  you can also pass a function to sort the array
   *  @example
   *  items.sort(Sort.by((a, b) => a.name.localeCompare(b.name)));
   *
   *  you can also pass a second parameter to specify the order of the sort
   *  @example
   *  sort by name in descending order
   *  items.sort(Sort.by('name', 'desc'));
   *
   *  @example
   *  for sorting by date, add the type option because sometimes it fails when validating the string using luxon
   *  items.sort(Sort.by('date', 'asc', { type: 'date' }));
   *
   *  OR
   *
   *  use
   *  @see {@link sortDate}
   *
   *  for precision use the options parameter to specify the type of the property you want to sort
   */
  public static by(prop: string | SortFn, order: 'asc' | 'desc' = 'asc', options?: SortOptions): SortFn {
    return (a: any, b: any): number => {
      const direction = SORT_DIRECTION[order];
      if (typeof prop === 'function') {
        return prop(a, b) * direction;
      } else if (typeof a[prop] === 'string' || (options && options.type === 'string')) {
        const aVal = a[prop] != null ? String(a[prop]).toLowerCase() : '';
        const bVal = b[prop] != null ? String(b[prop]).toLowerCase() : '';
        return aVal.localeCompare(bVal) * direction;
      } else if (typeof a[prop] === 'number' || (options && options.type === 'number')) {
        return (a[prop] - b[prop]) * direction;
      } else if (typeof a[prop] === 'boolean' || (options && options.type === 'boolean')) {
        // asc = false first, desc = true first
        return (a[prop] - b[prop]) * direction;
      } else if (options && options.type === 'date') {
        return this.byDate(prop, order)(a, b);
      }
      return 0;
    };
  }

  public static multiple(...args: SortFn[]) {
    return (a: any, b: any): any => {
      return args.reduce((acc, curr) => {
        return acc || curr(a, b);
      }, 0);
    };
  }

  /**
   * Luxon date validation sometimes fails esp. when using DateTime.fromISO('10').isValid -> true
   *
   * @example
   * items.sort(Sort.byDate('StartDate', 'asc'));
   */
  public static byDate(prop: string, order: 'asc' | 'desc' = 'asc'): SortFn {
    return (a: any, b: any): number => {
      const direction = SORT_DIRECTION[order];
      return (
        DateTime.fromISO(a[prop], { zone: 'utc' })
          .diff(DateTime.fromISO(b[prop], { zone: 'utc' }))
          .toMillis() * direction
      );
    };
  }
}
